import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Radio,
  Space,
  Divider,
  Result,
  Spin,
  Descriptions,
  message,
  Alert,
} from 'antd';
import {
  AlipayCircleOutlined,
  WechatOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../../services/subscription';
import type { PaymentMethod, PaymentOrder } from '../../types/subscription';
import { COLORS, PAYMENT_COLORS } from '../../constants/colors';

const { Title, Text, Paragraph } = Typography;

// 支付方式配置
const paymentMethods = [
  {
    key: 'alipay' as PaymentMethod,
    label: '支付宝',
    icon: <AlipayCircleOutlined style={{ fontSize: 32, color: PAYMENT_COLORS.alipay }} />,
    description: '支持支付宝扫码或跳转支付',
  },
  {
    key: 'wechat' as PaymentMethod,
    label: '微信支付',
    icon: <WechatOutlined style={{ fontSize: 32, color: PAYMENT_COLORS.wechat }} />,
    description: '支持微信扫码支付',
  },
  {
    key: 'bank_transfer' as PaymentMethod,
    label: '对公转账',
    icon: <BankOutlined style={{ fontSize: 32, color: COLORS.purple }} />,
    description: '银行对公转账，1-3个工作日到账',
  },
];

// 对公转账银行信息
const bankInfo = {
  bankName: '招商银行深圳分行',
  accountName: 'NexusInflu科技有限公司',
  accountNo: '7559 2836 0110 801',
  remark: '请在转账备注中填写订单号',
};

type PaymentStatus = 'idle' | 'paying' | 'success' | 'failed' | 'processing';

export const PaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const invoiceId = searchParams.get('invoice_id') || '';
  const amount = parseFloat(searchParams.get('amount') || '0');
  const subject = searchParams.get('subject') || '订阅费用';

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('alipay');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [orderInfo, setOrderInfo] = useState<PaymentOrder | null>(null);
  const [loading, setLoading] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingStartRef = useRef<number>(0);

  // 清除轮询
  const clearPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // 轮询支付状态
  const startPolling = useCallback(
    (orderNo: string) => {
      pollingStartRef.current = Date.now();
      pollingRef.current = setInterval(async () => {
        // 超过5分钟停止轮询
        if (Date.now() - pollingStartRef.current > 5 * 60 * 1000) {
          clearPolling();
          setPaymentStatus('processing');
          return;
        }

        try {
          const res = await subscriptionApi.queryPaymentOrder(orderNo);
          if (res.data) {
            const { status } = res.data;
            if (status === 'paid') {
              clearPolling();
              setPaymentStatus('success');
              setOrderInfo(res.data);
            } else if (status === 'failed' || status === 'cancelled') {
              clearPolling();
              setPaymentStatus('failed');
              setOrderInfo(res.data);
            }
          }
        } catch {
          // 轮询失败不中断
        }
      }, 3000);
    },
    [clearPolling]
  );

  // 组件卸载时清除轮询
  useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  // 创建支付订单
  const handlePay = async () => {
    if (amount <= 0) {
      message.error('支付金额无效');
      return;
    }

    setLoading(true);
    try {
      const res = await subscriptionApi.createPaymentOrder({
        amount,
        subject,
        payment_method: selectedMethod,
        invoice_id: invoiceId || undefined,
      });

      if (res.data) {
        setOrderInfo(res.data);
        setPaymentStatus('paying');

        // 支付宝/微信跳转
        if (selectedMethod !== 'bank_transfer' && res.data.pay_url) {
          window.open(res.data.pay_url, '_blank');
        }

        // 开始轮询
        startPolling(res.data.order_no);
      }
    } catch (error) {
      console.error('Failed to create payment order:', error);
      message.error('创建支付订单失败');
    } finally {
      setLoading(false);
    }
  };

  // 返回账单管理
  const handleBack = () => {
    clearPolling();
    navigate('/settings/billing');
  };

  // 渲染支付方式选择
  const renderMethodSelection = () => (
    <Card style={{ marginBottom: 24 }}>
      <Title level={5}>选择支付方式</Title>
      <Radio.Group
        value={selectedMethod}
        onChange={(e) => setSelectedMethod(e.target.value)}
        style={{ width: '100%' }}
      >
        <Row gutter={16}>
          {paymentMethods.map((method) => (
            <Col span={8} key={method.key}>
              <Radio.Button
                value={method.key}
                style={{
                  width: '100%',
                  height: 'auto',
                  padding: '16px',
                  textAlign: 'center',
                  borderRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 120,
                }}
              >
                <div style={{ marginBottom: 8 }}>{method.icon}</div>
                <div>
                  <Text strong>{method.label}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {method.description}
                  </Text>
                </div>
              </Radio.Button>
            </Col>
          ))}
        </Row>
      </Radio.Group>
    </Card>
  );

  // 渲染对公转账信息
  const renderBankTransfer = () => (
    <Card style={{ marginBottom: 24 }}>
      <Title level={5}>对公转账信息</Title>
      <Alert
        message="请将款项转至以下银行账户，并在备注中填写订单号"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="开户行">{bankInfo.bankName}</Descriptions.Item>
        <Descriptions.Item label="账户名称">{bankInfo.accountName}</Descriptions.Item>
        <Descriptions.Item label="银行账号">
          <Text copyable>{bankInfo.accountNo}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="转账备注">
          <Text copyable>{orderInfo?.order_no || '待创建'}</Text>
        </Descriptions.Item>
      </Descriptions>
      <Paragraph type="secondary" style={{ marginTop: 12 }}>
        {bankInfo.remark}，转账完成后1-3个工作日内将自动确认到账。
      </Paragraph>
    </Card>
  );

  // 渲染支付中状态
  const renderPayingStatus = () => (
    <Card style={{ marginBottom: 24, textAlign: 'center' }}>
      {selectedMethod === 'bank_transfer' ? (
        renderBankTransfer()
      ) : (
        <>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <Title level={5} style={{ marginTop: 16 }}>
            等待支付结果...
          </Title>
          <Paragraph type="secondary">
            请在新打开的页面完成支付，支付完成后将自动跳转
          </Paragraph>
          <Paragraph type="secondary">
            如未自动弹出支付页面，
            {orderInfo?.pay_url && (
              <Button type="link" onClick={() => window.open(orderInfo.pay_url, '_blank')}>
                点击此处重新打开
              </Button>
            )}
          </Paragraph>
        </>
      )}
    </Card>
  );

  // 渲染支付结果
  const renderPaymentResult = () => {
    if (paymentStatus === 'success') {
      return (
        <Result
          status="success"
          icon={<CheckCircleOutlined />}
          title="支付成功"
          subTitle={`订单号：${orderInfo?.order_no || '-'}，支付金额：¥${amount.toFixed(2)}`}
          extra={[
            <Button type="primary" key="back" onClick={handleBack}>
              返回账单管理
            </Button>,
          ]}
        />
      );
    }

    if (paymentStatus === 'failed') {
      return (
        <Result
          status="error"
          icon={<CloseCircleOutlined />}
          title="支付失败"
          subTitle="支付未能完成，请重试或选择其他支付方式"
          extra={[
            <Button type="primary" key="retry" onClick={() => setPaymentStatus('idle')}>
              重新支付
            </Button>,
            <Button key="back" onClick={handleBack}>
              返回账单管理
            </Button>,
          ]}
        />
      );
    }

    if (paymentStatus === 'processing') {
      return (
        <Result
          status="info"
          icon={<LoadingOutlined />}
          title="支付处理中"
          subTitle="支付结果确认中，请稍后在账单管理中查看支付状态"
          extra={[
            <Button type="primary" key="back" onClick={handleBack}>
              返回账单管理
            </Button>,
          ]}
        />
      );
    }

    return null;
  };

  // 如果是结果状态，直接展示结果
  if (['success', 'failed', 'processing'].includes(paymentStatus)) {
    return (
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>
          支付结果
        </Title>
        {renderPaymentResult()}
      </div>
    );
  }

  return (
    <Spin spinning={loading}>
      <div>
        <Space style={{ marginBottom: 24 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            返回
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            支付
          </Title>
        </Space>

        {/* 订单信息 */}
        <Card style={{ marginBottom: 24 }}>
          <Title level={5}>订单信息</Title>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="订单描述">{subject}</Descriptions.Item>
            <Descriptions.Item label="支付金额">
              <Text strong style={{ fontSize: 20, color: COLORS.primary }}>
                ¥{amount.toFixed(2)}
              </Text>
            </Descriptions.Item>
            {invoiceId && (
              <Descriptions.Item label="关联账单">{invoiceId}</Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* 支付中：显示等待 */}
        {paymentStatus === 'paying' ? (
          renderPayingStatus()
        ) : (
          <>
            {/* 支付方式选择 */}
            {renderMethodSelection()}

            {/* 对公转账时显示银行信息 */}
            {selectedMethod === 'bank_transfer' && paymentStatus === 'idle' && (
              <Card style={{ marginBottom: 24 }}>
                <Title level={5}>对公转账信息</Title>
                <Alert
                  message="提交订单后，请按照银行信息完成转账"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="开户行">{bankInfo.bankName}</Descriptions.Item>
                  <Descriptions.Item label="账户名称">{bankInfo.accountName}</Descriptions.Item>
                  <Descriptions.Item label="银行账号">
                    <Text copyable>{bankInfo.accountNo}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </>
        )}

        {/* 提交按钮 */}
        {paymentStatus === 'idle' && (
          <Card>
            <Button
              type="primary"
              size="large"
              block
              onClick={handlePay}
              disabled={amount <= 0}
            >
              {selectedMethod === 'bank_transfer'
                ? '提交转账订单'
                : `立即支付 ¥${amount.toFixed(2)}`}
            </Button>
          </Card>
        )}
      </div>
    </Spin>
  );
};
